import React, { useEffect, useMemo } from 'react';
import { X, Building2, QrCode, Banknote, CheckCircle2, Send } from 'lucide-react';
import Modal from '@/components/ui/Modal';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { FormField, Input, Select, Textarea } from '@/components/ui/FormComponents';
import { projectService } from '../services/projectService';
import {
  buildProjectCreditSchema,
  formatInrHint,
  CREDIT_RECIPIENT_MAX,
  CREDIT_REF_MAX,
  CREDIT_BANK_MAX,
  CREDIT_BRANCH_MAX,
  CREDIT_NOTES_MAX
} from '../validation/projectTransactionSchemas';

const PAYMENT_TYPES = ['Milestone payment', 'Advance', 'Balance', 'Other'];

/** Remount when `maxCredit` changes so zod resolver matches current net balance. */
const RecordProjectCreditModalInner = ({ project, maxCredit, onClose, onSuccess }) => {
  const creditSchema = useMemo(() => buildProjectCreditSchema(maxCredit), [maxCredit]);

  const {
    register,
    handleSubmit,
    watch,
    reset,
    setValue,
    clearErrors,
    formState: { errors, isSubmitting }
  } = useForm({
    resolver: zodResolver(creditSchema),
    defaultValues: {
      transactionDate: new Date().toISOString().split('T')[0],
      amount: '',
      paymentMethod: 'bank_transfer',
      paymentType: 'Milestone payment',
      refId: '',
      bankName: '',
      branch: '',
      recipient: '',
      internalNotes: '',
      // New dynamic fields
      upiPhone: '',
      receiverName: '',
      receiverPhone: '',
      chequeNumber: '',
      chequeDate: ''
    },
    mode: "onChange"
  });

  const [formError, setFormError] = React.useState("");

  const method = watch('paymentMethod');

  useEffect(() => {
    setFormError("");
    clearErrors();
    reset({
      transactionDate: new Date().toISOString().split('T')[0],
      amount: '',
      paymentMethod: 'bank_transfer',
      paymentType: 'Milestone payment',
      refId: '',
      bankName: '',
      branch: '',
      recipient: '',
      internalNotes: '',
      upiPhone: '',
      receiverName: '',
      receiverPhone: '',
      chequeNumber: '',
      chequeDate: ''
    });
  }, [reset, project?._id, maxCredit, clearErrors]);

  const onSubmit = async (data) => {
    setFormError("");
    try {
      await projectService.createProjectTransaction(project._id, {
        kind: 'credit',
        transactionDate: data.transactionDate,
        amount: data.amount,
        paymentMethod: data.paymentMethod,
        paymentType: data.paymentType,
        refId: data.refId || '',
        bankName: data.bankName || '',
        branch: data.branch || '',
        recipient: data.recipient,
        internalNotes: data.internalNotes || '',
        // Dynamic fields
        upiPhone: data.upiPhone || '',
        receiverName: data.receiverName || '',
        receiverPhone: data.receiverPhone || '',
        chequeNumber: data.chequeNumber || '',
        chequeDate: data.chequeDate || null
      });
      if (onSuccess) await onSuccess();
      onClose();
    } catch (e) {
      setFormError(e?.response?.data?.message || e.message || 'Could not record payment');
    }
  };

  const methods = [
    { id: 'bank_transfer', label: 'Bank transfer', icon: Building2 },
    { id: 'upi_qr', label: 'UPI / QR', icon: QrCode },
    { id: 'cash', label: 'Cash', icon: Banknote },
    { id: 'cheque', label: 'Cheque', icon: CheckCircle2 }
  ];

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1 min-w-0">
        <p className="text-[12px] font-medium text-slate-500">
          Credit (money in) — updates total paid and net balance.
        </p>
      </div>

      <form
        onSubmit={handleSubmit(onSubmit, () => {
          setFormError("Please check the form for errors and fill all required fields.");
        })}
        className="flex flex-col gap-4 px-4 pb-4"
      >
        {formError && (
          <div className="p-3 bg-rose-50 border border-rose-100 rounded-lg text-[11px] font-bold text-rose-600 animate-in fade-in slide-in-from-top-1">
            {formError}
          </div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FormField label="Transaction date" required error={errors.transactionDate}>
            <Input type="date" id="credit-date" {...register('transactionDate')} />
          </FormField>
          <FormField label="Amount (₹)" required error={errors.amount}>
            <Input
              type="number"
              step="0.01"
              min="0.01"
              // Polinaidu: 28-04-2026 (19:22) - Removed max attribute to allow overpayments
              id="credit-amount"
              placeholder="0.00"
              {...register('amount')}
            />
            <p className="text-[11px] font-medium text-slate-500 mt-1">
              {/* Polinaidu: 28-04-2026 (19:22) - Updated hint to show net balance without restricting payments */}
              {maxCredit !== 0 ? (
                <>
                  Remaining net balance: {formatInrHint(maxCredit)}
                </>
              ) : (
                <>Contract balance fully collected. Additional payments can still be recorded.</>
              )}
            </p>
          </FormField>
        </div>

        <div className="flex flex-col gap-2">
          <span className="text-[12px] font-medium text-slate-500">Payment method</span>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {methods.map((m) => {
              const Icon = m.icon;
              const active = method === m.id;
              return (
                <button
                  key={m.id}
                  type="button"
                  id={`credit-method-${m.id}`}
                  onClick={() => setValue('paymentMethod', m.id, { shouldValidate: true })}
                  className={`h-10 rounded-lg border text-[12px] font-medium flex items-center justify-center gap-1.5 transition-all duration-300 ease-in-out ${active
                      ? 'border-emerald-500 bg-emerald-50 text-emerald-800'
                      : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
                    }`}
                >
                  <Icon className="w-[16px] h-[16px] shrink-0" />
                  <span className="truncate">{m.label}</span>
                </button>
              );
            })}
          </div>
          <input type="hidden" {...register('paymentMethod')} readOnly />
        </div>

        <FormField label="Payment type" required error={errors.paymentType}>
          <Select id="credit-payment-type" {...register('paymentType')}>
            {PAYMENT_TYPES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </Select>
        </FormField>

        {/* Conditional Fields based on Payment Method */}
        {method === 'bank_transfer' && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 animate-in fade-in slide-in-from-top-2 duration-300">
              <FormField label="Transaction / ref ID" error={errors.refId}>
                <Input
                  id="credit-ref-bank"
                  placeholder="TRX123..."
                  maxLength={CREDIT_REF_MAX}
                  {...register('refId')}
                />
              </FormField>
              <FormField label="Bank name" error={errors.bankName}>
                <Input
                  id="credit-bank-name"
                  placeholder="e.g. HDFC"
                  maxLength={CREDIT_BANK_MAX}
                  {...register('bankName')}
                />
              </FormField>
            </div>
            <FormField label="Branch" error={errors.branch}>
              <Input
                id="credit-branch"
                placeholder="Branch name"
                maxLength={CREDIT_BRANCH_MAX}
                {...register('branch')}
              />
            </FormField>
          </>
        )}

        {method === 'upi_qr' && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 animate-in fade-in slide-in-from-top-2 duration-300">
              <FormField label="UPI UTR / Ref No." error={errors.refId}>
                <Input
                  id="credit-ref-upi"
                  placeholder="TRX123..."
                  maxLength={CREDIT_REF_MAX}
                  {...register('refId')}
                />
              </FormField>
              <FormField label="UPI Phone Number" error={errors.upiPhone}>
                <Input
                  id="credit-upi-phone"
                  placeholder="10-digit Phone"
                  maxLength={10}
                  {...register('upiPhone')}
                />
              </FormField>
            </div>
            <FormField label="Linked Bank" error={errors.bankName}>
              <Input
                id="credit-upi-bank"
                placeholder="Bank Name"
                maxLength={CREDIT_BANK_MAX}
                {...register('bankName')}
              />
            </FormField>
          </>
        )}

        {method === 'cash' && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 animate-in fade-in slide-in-from-top-2 duration-300">
            <FormField label="Receiver Name" error={errors.receiverName}>
              <Input
                id="credit-cash-receiver"
                placeholder="Who received cash"
                {...register('receiverName')}
              />
            </FormField>
            <FormField label="Receiver Phone" error={errors.receiverPhone}>
              <Input
                id="credit-cash-phone"
                placeholder="10-digit number"
                maxLength={10}
                {...register('receiverPhone')}
              />
            </FormField>
          </div>
        )}

        {method === 'cheque' && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 animate-in fade-in slide-in-from-top-2 duration-300">
              <FormField label="Reference ID" error={errors.refId}>
                <Input
                  id="credit-ref-cheque"
                  placeholder="TRX123..."
                  maxLength={CREDIT_REF_MAX}
                  {...register('refId')}
                />
              </FormField>
              <FormField label="Cheque Number" error={errors.chequeNumber}>
                <Input
                  id="credit-cheque-num"
                  placeholder="6-digit No."
                  maxLength={6}
                  {...register('chequeNumber')}
                />
              </FormField>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 animate-in fade-in slide-in-from-top-2 duration-300">
              <FormField label="Cheque Date" error={errors.chequeDate}>
                <Input
                  type="date"
                  id="credit-cheque-date"
                  {...register('chequeDate')}
                />
              </FormField>
              <FormField label="Issuing Bank" error={errors.bankName}>
                <Input
                  id="credit-cheque-bank"
                  placeholder="Bank name on cheque"
                  maxLength={CREDIT_BANK_MAX}
                  {...register('bankName')}
                />
              </FormField>
            </div>
          </>
        )}

        <FormField label="Recipient (entity / person)" required error={errors.recipient}>
          <Input
            id="credit-recipient"
            placeholder="e.g. Verve Kitchens"
            maxLength={CREDIT_RECIPIENT_MAX}
            autoComplete="off"
            {...register('recipient')}
          />
        </FormField>

        <FormField label="Internal notes (optional)" error={errors.internalNotes}>
          <Textarea
            id="credit-notes"
            rows={2}
            maxLength={CREDIT_NOTES_MAX}
            placeholder="Optional — if filled, at least 3 characters"
            {...register('internalNotes')}
          />
        </FormField>

        <div className="flex flex-col-reverse sm:flex-row items-center gap-3 pt-4 border-t border-slate-100 mt-2">
          <button
            type="button"
            id="credit-cancel"
            onClick={onClose}
            className="w-full sm:flex-1 cursor-pointer py-2.5 border border-slate-200 hover:bg-slate-50 text-slate-500 rounded-md text-[12px] font-medium transition-all duration-300 ease-in-out"
          >
            Cancel
          </button>
          <button
            type="submit"
            id="credit-submit"
            disabled={isSubmitting}
            className="w-full sm:flex-1 cursor-pointer py-2.5 min-h-[40px] bg-[#64748b] hover:bg-[#475569] text-white rounded-md text-[12px] font-medium inline-flex items-center justify-center gap-2 transition-all duration-300 ease-in-out disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Send className="w-4 h-4" />
            Record Payment
          </button>
        </div>
      </form>
    </div>
  );
};

export const RecordProjectCreditModal = ({ isOpen, onClose, project, onSuccess }) => {
  const maxCredit = useMemo(() => {
    // Case 1: Full project object from getProjectById (has paymentSummary)
    const ps = project?.paymentSummary;
    // Polinaidu: 28-04-2026 (19:22) - Removed Math.max(0, ...) to support negative balances
    if (ps && ps.netBalance != null && Number.isFinite(Number(ps.netBalance))) {
      return Number(ps.netBalance);
    }

    // Case 2: Flattened project object from getAccountsOverview or getProjects
    if (project?.netBalance != null && Number.isFinite(Number(project.netBalance))) {
      return Number(project.netBalance);
    }

    const cv = Number(project?.projectValue || project?.contractValue) || 0;
    const tp = ps?.totalPaid ?? project?.totalReceived;
    if (tp != null && Number.isFinite(Number(tp))) {
      return cv - Number(tp);
    }
    return 0;
  }, [project]);

  if (!isOpen || !project) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Record project transaction"
      size="lg"
    >
      <RecordProjectCreditModalInner
        key={`${project._id}-${maxCredit}`}
        project={project}
        maxCredit={maxCredit}
        onClose={onClose}
        onSuccess={onSuccess}
      />
    </Modal>
  );
};
